import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import DiwaliDonationWidget from "@/components/donation/DiwaliDonationWidget";
import DiwaliProgress from "@/components/campaign/DiwaliProgress";
import api, { getCasePhotoUrls, CANONICAL_URL } from "@/lib/api";
import { setMetaTag, resolveImageWithFallback } from "@/lib/seo";
import { useDiwaliCampaign } from "@/hooks/useDiwaliCampaign";

export default function Diwali() {
  const { status, loading } = useDiwaliCampaign();
  const [josh, setJosh] = useState(null);

  useEffect(() => {
    api.get("/public/cases/josh").then((res) => setJosh(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    document.title = "Diwali for the whole AMTM family | AMTM Rescue Hub";
    setMetaTag("og:title", "Diwali for the whole AMTM family");
    setMetaTag("og:description", "Festive meals for 500+ rescued animals and a thank you to their caregivers.");
    resolveImageWithFallback(`${CANONICAL_URL}/images/diwali-og.jpg`, `${CANONICAL_URL}/images/sanctuary.jpg`, (url) => setMetaTag("og:image", url));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-amtm-bg">
        <Loader2 className="h-8 w-8 animate-spin text-amtm-teal" />
      </div>
    );
  }

  if (status && !status.active) {
    return <Navigate to="/guardians" replace />;
  }

  const joshPhoto = josh ? getCasePhotoUrls(josh)[0] : null;

  return (
    <div className="min-h-screen bg-amtm-bg" data-testid="diwali-page">
      <PublicHeader />

      <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24 grid md:grid-cols-2 gap-12 items-start">
        <div>
          <p className="text-xs md:text-sm font-semibold uppercase tracking-[0.18em] text-amtm-teal mb-5" data-testid="diwali-page-eyebrow">
            THIS DIWALI
          </p>
          <h1 className="font-display text-4xl md:text-5xl leading-tight text-amtm-text mb-5" data-testid="diwali-page-headline">
            Diwali for the whole AMTM family
          </h1>
          <p className="text-base md:text-lg text-amtm-textMuted leading-relaxed mb-8" data-testid="diwali-page-description">
            Festive meals for 500+ rescued animals, and a thank you to the caregivers who look after them every single
            day, festival or not. Every rupee goes to Diwali meals for the animals and gifts for our caregivers.
          </p>
          <DiwaliProgress />
        </div>
        <div>
          <DiwaliDonationWidget testIdPrefix="diwali-page" />
        </div>
      </section>

      {josh && (
        <section className="bg-white py-16 md:py-20 border-t border-amtm-border" data-testid="diwali-josh-section">
          <div className="max-w-4xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-8 items-center">
            {joshPhoto && (
              <Link to="/case/josh" data-testid="diwali-josh-photo-link">
                <img
                  src={joshPhoto}
                  alt="Josh, who now lives at our Kolad sanctuary"
                  className="w-full h-72 md:h-80 object-cover rounded-2xl shadow-sm"
                  loading="lazy"
                />
              </Link>
            )}
            <div>
              <h2 className="font-display text-2xl md:text-3xl text-amtm-text mb-4">Diwali nights are the hardest for them</h2>
              <p className="text-base text-amtm-textMuted leading-relaxed mb-4">
                The noise sends frightened animals running into traffic. Josh, who now lives at our Kolad sanctuary,
                lost an eye after firecrackers were burst on his face. Your gift keeps our team ready through the
                festival, so no frightened or injured animal is turned away.
              </p>
              <Link to="/case/josh" className="text-sm font-semibold text-amtm-teal hover:underline" data-testid="diwali-josh-case-link">
                Read Josh's story &rarr;
              </Link>
            </div>
          </div>
        </section>
      )}

      <PublicFooter />
    </div>
  );
}
